import React, { useCallback, useEffect, useMemo, useState } from 'react';

import RuleRow from './RuleRow';

import { formatDateWithTz } from 'Helpers/dateUtils';
import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import ToggleInput from 'CommonComponents/ToggleInput';

const SCOPE_KEY = (scope) => `${scope.type}:${scope.id === null ? '' : scope.id}`;

const REVENUE_SCOPES = ['ticketing', 'revdepartment'];

export default function ContingenciesApprovalsTab(props) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState([]);
    const [globals, setGlobals] = useState({ approval_increases: false, approval_decreases: false });
    const [globalsDirty, setGlobalsDirty] = useState(false);
    const [summary, setSummary] = useState({});
    const [recordingSaving, setRecordingSaving] = useState(false);
    const [users, setUsers] = useState([]);
    const [edits, setEdits] = useState({});
    const [expanded, setExpanded] = useState({});
    const [collapsedDepartments, setCollapsedDepartments] = useState({});
    const [staleKeys, setStaleKeys] = useState([]);
    const [errorMessages, setErrorMessages] = useState([]);

    const loadRules = useCallback(() => {
        setLoading(true);

        return axios
            .get(props.rulesUrl)
            .then((res) => {
                setRows(res.data.rows);
                setGlobals(res.data.globals);
                setSummary(res.data.summary);
                setEdits({});
                setGlobalsDirty(false);
                setStaleKeys([]);
                setErrorMessages([]);
            })
            .catch(() => setErrorMessages([$t('The budget change rules could not be loaded. Refresh the page to try again.')]))
            .finally(() => setLoading(false));
    }, [props.rulesUrl]);

    useEffect(() => {
        loadRules();

        axios
            .get(props.usersUrl)
            .then((res) => setUsers(res.data.data || []))
            .catch(() => setUsers([]));
    }, [loadRules, props.usersUrl]);

    // A row's live state is its server value with any unsaved edit laid over it.
    const rowState = useCallback((row) => ({ ...row, ...(edits[SCOPE_KEY(row.scope)] || {}) }), [edits]);

    const editRow = (row, changes) => {
        const key = SCOPE_KEY(row.scope);

        setEdits((current) => ({
            ...current,
            [key]: {
                ...(current[key] || {}),
                ...changes,
                // The balance the screen held when this row's editing began, so the
                // server can reject a save built on a figure that has since moved.
                loaded_balance: current[key]?.loaded_balance !== undefined ? current[key].loaded_balance : row.balance,
            },
        }));
    };

    const editGlobals = (changes) => {
        setGlobalsDirty(true);
        setGlobals((current) => ({ ...current, ...changes }));
    };

    const dirtyKeys = useMemo(() => Object.keys(edits), [edits]);
    const isDirty = dirtyKeys.length > 0 || globalsDirty;

    const liveRows = useMemo(() => rows.map(rowState), [rows, rowState]);

    const totals = useMemo(() => {
        const balanceOf = (row) => (row.balance === null ? 0 : Number(row.balance));
        const contingencyRows = liveRows.filter((row) => row.supports_contingency);

        return {
            total: contingencyRows.reduce((carry, row) => carry + balanceOf(row), 0),
            central: contingencyRows.filter((row) => row.scope.type === 'central').reduce((carry, row) => carry + balanceOf(row), 0),
            departmental: contingencyRows
                .filter((row) => row.scope.type === 'department' || row.scope.type === 'costcenter')
                .reduce((carry, row) => carry + balanceOf(row), 0),
            // Every row carries an approval rule, Event Profit included.
            active: liveRows.filter((row) => row.active).length,
            rules: liveRows.length,
        };
    }, [liveRows]);

    const save = () => {
        setSaving(true);
        setErrorMessages([]);
        setStaleKeys([]);

        const payload = {
            // Both keys stay present so the request's `present` rule passes; null
            // tells the server to leave the flag as it is.
            globals: globalsDirty
                ? { approval_increases: globals.approval_increases, approval_decreases: globals.approval_decreases }
                : { approval_increases: null, approval_decreases: null },
            rows: dirtyKeys
                .map((key) => liveRows.find((row) => SCOPE_KEY(row.scope) === key))
                .filter(Boolean)
                .map((row) => ({
                    scope: row.scope,
                    active: !!row.active,
                    contingency_active: row.supports_contingency && !!row.contingency_active,
                    balance: row.supports_contingency ? Number(row.balance || 0) : null,
                    loaded_balance: edits[SCOPE_KEY(row.scope)]?.loaded_balance ?? null,
                    approval_type: row.approval_type,
                    threshold: row.threshold === '' ? null : row.threshold,
                    // The engine ends its chain at the approver whose value covers the
                    // change, so the top level stores no escalation value.
                    levels: (row.levels || []).map((level, index, all) => ({
                        user_id: level.user_id,
                        value: index === all.length - 1 ? null : level.value === '' || level.value === undefined ? null : Number(level.value),
                    })),
                })),
        };

        axios
            .put(props.saveUrl, payload)
            .then((res) => {
                setRows(res.data.rows);
                setGlobals(res.data.globals);
                setSummary(res.data.summary);
                setEdits({});
                setGlobalsDirty(false);

                if (props.onSaved) {
                    props.onSaved(res.data);
                }
            })
            .catch((error) => {
                if (error.response?.status === 409) {
                    setStaleKeys((error.response.data.stale_rows || []).map((item) => SCOPE_KEY(item.scope)));
                    setErrorMessages([error.response.data.message]);

                    return;
                }

                // A rejected save carries one message per field it objected to. The
                // top-level `message` is only the first of them with a count
                // appended, so listing the fields shows every problem at once.
                const fieldMessages = [...new Set(Object.values(error.response?.data?.errors || {}).flat())];

                setErrorMessages(fieldMessages.length > 0 ? fieldMessages : [error.response?.data?.message || $t('The changes could not be saved.')]);
            })
            .finally(() => setSaving(false));
    };

    // Applied on its own, not through Save changes: it changes what is recorded from
    // the moment it is switched. Only the switch's own figures are taken from the
    // response, so unsaved row edits survive.
    const setRecording = (enabled) => {
        setRecordingSaving(true);
        setErrorMessages([]);

        axios
            .put(props.recordingUrl, { enabled })
            .then((res) => {
                const recordingByScope = Object.fromEntries(res.data.rows.map((row) => [SCOPE_KEY(row.scope), row.recording]));

                setGlobals((current) => ({ ...current, record_all: res.data.record_all }));
                setRows((current) => current.map((row) => ({ ...row, recording: recordingByScope[SCOPE_KEY(row.scope)] ?? row.recording })));
                setSummary(res.data.summary);
            })
            .catch((error) => setErrorMessages([error.response?.data?.message || $t('The change could not be saved.')]))
            .finally(() => setRecordingSaving(false));
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    const recordAll = globals.record_all || { enabled: false, at: null, by_name: null };

    const eventProfitRow = liveRows.find((row) => row.scope.type === 'event_profit');
    const centralRow = liveRows.find((row) => row.scope.type === 'central');
    const revenueRows = liveRows.filter((row) => REVENUE_SCOPES.includes(row.scope.type));
    const expenditureRows = liveRows.filter((row) => row.scope.type === 'department' || row.scope.type === 'costcenter');

    const renderRow = (row) => {
        const key = SCOPE_KEY(row.scope);

        return (
            <RuleRow
                key={key}
                row={row}
                users={users}
                readOnly={props.readOnly}
                stale={staleKeys.includes(key)}
                expanded={!!expanded[key]}
                collapsed={row.scope.type === 'costcenter' && !!collapsedDepartments[row.parent_id]}
                onToggleExpanded={() => setExpanded((current) => ({ ...current, [key]: !current[key] }))}
                onToggleChildren={
                    row.scope.type === 'department'
                        ? () => setCollapsedDepartments((current) => ({ ...current, [row.scope.id]: !current[row.scope.id] }))
                        : null
                }
                childrenCollapsed={!!collapsedDepartments[row.scope.id]}
                hasChildren={row.scope.type === 'department' && expenditureRows.some((child) => child.parent_id === row.scope.id)}
                onChange={(changes) => editRow(row, changes)}
            />
        );
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-6">
            <div className={`bc-recording ${recordAll.enabled ? 'bc-recording--on' : ''}`}>
                <div>
                    <ToggleInput
                        label={$t('Record all budget changes')}
                        checked={!!recordAll.enabled}
                        disabled={props.readOnly || recordingSaving}
                        noBottomSpacing
                        onChange={(e) => setRecording(e.target.checked)}
                    />
                    <p className="bc-recording__state">
                        {recordAll.enabled
                            ? $t(
                                  'On since {{date}} by {{name}}. Every budget change from this point is recorded in the Budget Changes log of its department, cost centre or revenue area.',
                                  {
                                      date: formatDateWithTz(recordAll.at, 'YYYY-MM-DDTHH:mm:ssZ', 'D MMM YYYY HH:mm'),
                                      name: recordAll.by_name || $t('Unknown'),
                                  },
                              )
                            : $t('Off. Changes are only recorded for scopes where change control is activated in the table below.')}
                    </p>
                </div>
                <p className="bc-recording__help">
                    {$t(
                        'When on, every budget change made from the activation date is written to the Budget Changes log of the department, cost centre or revenue area it belongs to — whether or not an approval rule exists for that scope. Changes with no approval rule are applied immediately and recorded as',
                    )}{' '}
                    <span className="bc-chip bc-chip--grey">{$t('No approver set up')}</span>.
                </p>
            </div>

            <div className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-5">
                <div className="bc-stat">
                    <div className="bc-stat__label">{$t('Total contingency balance')}</div>
                    <div className="bc-stat__value">{formatToFixedMoneyWithLocale(totals.total, 2)}</div>
                </div>
                <div className="bc-stat">
                    <div className="bc-stat__label">{$t('Central')}</div>
                    <div className="bc-stat__value">{formatToFixedMoneyWithLocale(totals.central, 2)}</div>
                </div>
                <div className="bc-stat">
                    <div className="bc-stat__label">{$t('Departmental')}</div>
                    <div className="bc-stat__value">{formatToFixedMoneyWithLocale(totals.departmental, 2)}</div>
                </div>
                <div className="bc-stat">
                    <div className="bc-stat__label">{$t('Active approval rules')}</div>
                    <div className="bc-stat__value bc-stat__value--blue">{$t('{{active}} of {{total}}', { active: totals.active, total: totals.rules })}</div>
                </div>
                <div className="bc-stat">
                    <div className="bc-stat__label">{$t('Changes recorded since activation')}</div>
                    <div className="bc-stat__value">{summary.recorded_since_activation ?? '—'}</div>
                    {summary.recorded_since_activation !== null && summary.recorded_since_activation !== undefined && (
                        <div className="bc-stat__sub bc-stat__sub--amber">{$t('{{count}} pending approval', { count: summary.recorded_pending ?? 0 })}</div>
                    )}
                </div>
            </div>

            <p className="bc-help">{$t('Balances are live and move as budget lines draw on or return to contingency.')}</p>

            {errorMessages.length > 0 && (
                <Alert type="warning">
                    {errorMessages.length === 1 ? (
                        errorMessages[0]
                    ) : (
                        <ul className="tw-mb-0 tw-list-disc tw-ps-5">
                            {errorMessages.map((message) => (
                                <li key={message}>{message}</li>
                            ))}
                        </ul>
                    )}
                </Alert>
            )}

            {staleKeys.length > 0 && (
                <div>
                    <Button variant="outline" small onClick={loadRules}>
                        {$t('Refresh balances')}
                    </Button>
                </div>
            )}

            <div className="bc-globals">
                <div>
                    <ToggleInput
                        label={$t('Require approval for budget increases')}
                        checked={!!globals.approval_increases}
                        disabled={props.readOnly}
                        onChange={(e) => editGlobals({ approval_increases: e.target.checked })}
                    />
                    <p className="bc-help">{$t('Applies only to rows below that are active and have at least one approver.')}</p>
                </div>
                <div>
                    <ToggleInput
                        label={$t('Require approval for budget decreases')}
                        checked={!!globals.approval_decreases}
                        disabled={props.readOnly}
                        onChange={(e) => editGlobals({ approval_decreases: e.target.checked })}
                    />
                    <p className="bc-help">{$t('Applies only to rows below that are active and have at least one approver.')}</p>
                </div>
            </div>

            <div className="table__card">
                <div className="inbud-table_wrapper bc-table-wrapper">
                    <table className="inbud-table bc-table">
                        <thead>
                            <tr>
                                <th scope="col">{$t('Scope')}</th>
                                <th scope="col">{$t('Activate change control')}</th>
                                <th scope="col" className="bc-num">
                                    {$t('Base budget')}
                                </th>
                                <th scope="col">{$t('Activate contingency')}</th>
                                <th scope="col" className="bc-num">
                                    {$t('Contingency balance')}
                                </th>
                                <th scope="col" className="bc-num">
                                    {$t('% of base')}
                                </th>
                                <th scope="col">{$t('Approval type')}</th>
                                <th scope="col">{$t('Approvers')}</th>
                                <th scope="col">{$t('Recording')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!!eventProfitRow && renderRow(eventProfitRow)}
                            {!!centralRow && renderRow(centralRow)}

                            {revenueRows.length > 0 && (
                                <tr className="bc-row--group">
                                    <th colSpan={9} scope="colgroup">
                                        {$t('Revenue')}
                                    </th>
                                </tr>
                            )}
                            {revenueRows.map(renderRow)}

                            {expenditureRows.length > 0 && (
                                <tr className="bc-row--group">
                                    <th colSpan={9} scope="colgroup">
                                        {$t('Expenditure — departments and cost centres')}
                                    </th>
                                </tr>
                            )}
                            {expenditureRows.map(renderRow)}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="bc-help">
                {$t(
                    'The Recording column is read-only and reflects the global switch: a scope with change control active and an approver records with approval; every other scope records without approval once the global switch is on.',
                )}
            </p>

            <div className="bc-hint">
                <span className="bc-hint__icon" aria-hidden="true">
                    &#9432;
                </span>
                {$t(
                    'Movements in and out of any contingency are recorded automatically on the Movements tab whenever a budget line draws on or returns to contingency. Editing a balance here is logged as a manual movement.',
                )}
            </div>

            <div className="bc-actionbar">
                <p className="bc-help">{$t('Changes take effect for new budget change requests only.')}</p>
                {!props.readOnly && (
                    <div className="tw-flex tw-items-center tw-gap-x-2">
                        <Button variant="outline" small disabled={!isDirty || saving} onClick={loadRules}>
                            {$t('Discard changes')}
                        </Button>
                        <Button variant="primary" small loading={saving} disabled={!isDirty || saving} onClick={save}>
                            {$t('Save changes')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
